import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Type, ButtonPrimary } from '@spotify-internal/encore-creator-web';
import styled from 'styled-components';
import { createStructuredSelector } from 'reselect';
import { Helmet } from 'react-helmet';
import tokens from '@spotify-internal/encore-foundation';
import { ConnectedAddPodcastModal } from './AddPodcastModal';
import { trackEvent } from '../../lib/Tracking';
import { selectMe } from '../../store/selectors';
import { Me } from '../../types';
import { TSFix } from '@spotify-internal/ts-migration-utils/types';
import { injectIntl, IntlShape, defineMessage, FormattedMessage } from 'react-intl';

const InstructionGuide = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: 767px) {
    flex-direction: row;
  }
`;

const IllustrationContainer = styled.div`
  order: -1;
  margin-left: 2em;
  margin-bottom: 2em;
  width: 50%;

  @media (min-width: 767px) {
    width: initial;
    order: 1;
  }
`;

const GuideSectionText = styled(Type.p)`
  max-width: 35ch;
  margin-bottom: ${tokens.spacer24};
`;

const GuideImage = styled.img`
  display: block;
  width: 100%;
  max-width: 450px;
`;

interface Props {
  me: Me;
  intl: IntlShape;
}

export class AddPodcastLandingPage extends React.Component<Props> {
  static propTypes = {
    history: PropTypes.shape({}),
    location: PropTypes.shape({}),
    me: PropTypes.shape({
      uri: PropTypes.string,
    }),
  };

  state = {
    showModal: false,
  };

  componentDidMount() {
    trackEvent({ category: 'page-type', action: 'view', label: 'submit' });
  }

  toggleModal = /* istanbul ignore next */ () =>
    this.setState({ showModal: !this.state.showModal });

  handleSubmitClick = () => {
    trackEvent({ category: 'submission', action: 'click', label: 'get-started' });
    this.toggleModal();
  };

  render() {
    const pageTitle = defineMessage({
      id: 'add-podcast.page-title',
      defaultMessage: 'Add or claim your podcast',
    });
    return (
      <div
        data-qa="add-podcast-container"
        data-testid="add-podcast"
        style={{ marginBottom: '32px' }}
      >
        <Helmet title={this.props.intl.formatMessage(pageTitle)} />
        <Type as="h1" variant={Type.heading1} style={{ marginBottom: '32px' }}>
          <FormattedMessage id="add-podcast.title" defaultMessage="Add your podcast to see stats" />
        </Type>
        <InstructionGuide>
          <div>
            <Type as="h1" variant={Type.heading3}>
              <FormattedMessage
                id="add-podcast.instruction-title-1"
                defaultMessage="1. Link to your RSS feed"
              />
            </Type>
            <GuideSectionText variant={Type.body1}>
              <FormattedMessage
                id="add-podcast.instruction-content-1"
                defaultMessage="This way, your podcast will be on Spotify, and future episodes will appear automatically. No need to upload anything."
              />
            </GuideSectionText>
            <Type as="h1" variant={Type.heading3}>
              <FormattedMessage
                id="add-podcast.instruction-title-2"
                defaultMessage="2. Get stats, updated daily"
              />
            </Type>
            <GuideSectionText variant={Type.body1}>
              <FormattedMessage
                id="add-podcast.instruction-content-2"
                defaultMessage="Once your podcast is on Spotify, see how it’s performing and learn more about who’s listening, and from where."
              />
            </GuideSectionText>
            <ButtonPrimary data-qa="submit-podcast-button" onClick={this.handleSubmitClick}>
              <FormattedMessage
                id="add-podcast.call-to-action"
                description="Button title to start process of adding a new podcast"
                defaultMessage="Get Started"
              />
            </ButtonPrimary>
          </div>
          <IllustrationContainer>
            <GuideImage src="images/add-podcast-illustration.png" alt="" />
          </IllustrationContainer>
        </InstructionGuide>

        {this.state.showModal && (
          // TSFix
          <ConnectedAddPodcastModal
            // @ts-ignore
            {...this.props}
            // @ts-ignore
            onClose={this.toggleModal}
            // @ts-ignore
            data-qa="submission-modal-container"
          />
        )}
      </div>
    );
  }
}

const selectors = createStructuredSelector<TSFix, TSFix>({
  me: selectMe,
});

export const ConnectedAddPodcastLandingPage = connect(selectors)(
  injectIntl(AddPodcastLandingPage as TSFix),
);
